// Small pieces every Books tab shares. Keep this file boring.
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Note } from '../../components/ui';
import { dollars, toCents } from '../../lib/format';
import { accounts as accountsApi } from '../../lib/ledger/api';
import { ACCOUNT_TYPE_LABELS, type Account, type AccountType } from '../../lib/ledger/types';

/** Shown wherever a write control is disabled for a view-only user. */
export function ReadOnlyNote() {
  return <Note>You can view the books. Ask Leah or Laura for edit access if you need to change them.</Note>;
}

/** Load the chart once per tab; refetch with `reload()`. */
export function useAccounts(): { accounts: Account[]; byId: Map<string, Account>; byCode: Map<string, Account>; loading: boolean; error: unknown; reload: () => void } {
  const [list, setList] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    accountsApi.list()
      .then((a) => alive && setList(a))
      .catch((e) => alive && setError(e))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [tick]);
  const byId = useMemo(() => new Map(list.map((a) => [a.id, a])), [list]);
  const byCode = useMemo(() => new Map(list.map((a) => [a.code, a])), [list]);
  return { accounts: list, byId, byCode, loading, error, reload: () => setTick((t) => t + 1) };
}

const TYPE_ORDER: AccountType[] = ['asset', 'liability', 'equity', 'income', 'cogs', 'expense'];

/** A <select> of accounts grouped by type. `types` narrows the list. */
export function AccountSelect({ value, onChange, accounts, types, id, disabled, allowEmpty = true, activeOnly = true }: {
  value: string;
  onChange: (id: string) => void;
  accounts: Account[];
  types?: AccountType[];
  id?: string;
  disabled?: boolean;
  allowEmpty?: boolean;
  activeOnly?: boolean;
}) {
  const groups = TYPE_ORDER
    .filter((t) => !types || types.includes(t))
    .map((t) => ({ type: t, rows: accounts.filter((a) => a.type === t && (!activeOnly || a.is_active || a.id === value)) }))
    .filter((g) => g.rows.length > 0);
  return (
    <select id={id} className="lp-inp" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      {allowEmpty && <option value="">Choose an account</option>}
      {groups.map((g) => (
        <optgroup key={g.type} label={ACCOUNT_TYPE_LABELS[g.type]}>
          {g.rows.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

/** Dollars in, cents out. Keeps the raw text so typing feels normal. */
export function MoneyInput({ cents, onChange, id, disabled, placeholder = '0.00', allowNegative = false }: {
  cents: number | null;
  onChange: (cents: number | null) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  allowNegative?: boolean;
}) {
  const [text, setText] = useState(cents == null ? '' : (cents / 100).toFixed(2));
  useEffect(() => {
    if (cents == null && text !== '') return;
    if (cents != null && toCents(text) !== cents) setText((cents / 100).toFixed(2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cents]);
  return (
    <input
      id={id}
      className="lp-inp lp-money"
      inputMode="decimal"
      placeholder={placeholder}
      value={text}
      disabled={disabled}
      onChange={(e) => {
        const v = e.target.value;
        setText(v);
        if (v.trim() === '') { onChange(null); return; }
        const neg = v.trim().startsWith('-');
        const c = toCents(v.replace(/^-/, ''));
        onChange(c == null ? null : (neg && allowNegative ? -c : c));
      }}
    />
  );
}

/** Right-aligned money cell. Negative shows in parentheses, the accountant's way. */
export function Money({ cents, blankZero = false }: { cents: number | null | undefined; blankZero?: boolean }) {
  const n = Number(cents ?? 0) || 0; // `|| 0` turns -0 into 0 so nothing prints as -$0.00
  if (blankZero && n === 0) return <span className="lp-money muted">—</span>;
  return <span className="lp-money">{n < 0 ? `(${dollars(-n)})` : dollars(n)}</span>;
}

export function csvEscape(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadCsv(filename: string, rows: unknown[][]): void {
  const body = rows.map((r) => r.map(csvEscape).join(',')).join('\n');
  const blob = new Blob([`﻿${body}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function PrintButton({ label = 'Print' }: { label?: string }) {
  return <button type="button" className="lp-btn lp-btn--quiet lp-no-print" onClick={() => window.print()}>{label}</button>;
}

/** "‹ Back to Reports" inside a tab drill-in. Visible, 44pt tall, always present. */
export function BackTo({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="lp-back" onClick={onClick} style={{ minHeight: 44 }}>
      <span aria-hidden="true">‹</span> Back to {label}
    </button>
  );
}

/** Today's date in the person's own time zone, never UTC's tomorrow. */
export function localIso(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function today(): string {
  return localIso();
}
export function yearStart(d = new Date()): string {
  return `${d.getFullYear()}-01-01`;
}
export function monthStart(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}
export function monthEndOf(first: string): string {
  const [y, m] = first.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${first.slice(0, 8)}${String(last).padStart(2, '0')}`;
}

export function Row({ children }: { children: ReactNode }) {
  return <div className="lp-row">{children}</div>;
}
